/**
 * Page Audit OCR ↔ Notes : détecte et répare les incohérences entre les
 * relevés OCR validés et les NoteExamen en base.
 */
import React, { useEffect, useState } from 'react';
import {
  ShieldAlert, RefreshCw, Wrench, CheckCircle2, AlertTriangle,
  Loader, FileWarning, Clock, ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { statistiquesService, AuditNotesOcrResponse, AuditNotesDevoirsResponse } from '../../services/api.service';

const AuditNotesOCR: React.FC = () => {
  const [data, setData] = useState<AuditNotesOcrResponse | null>(null);
  const [dataDev, setDataDev] = useState<AuditNotesDevoirsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reparing, setReparing] = useState(false);
  const [reparingD, setReparingD] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'examens' | 'devoirs'>('examens');

  const fetchData = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [resE, resD] = await Promise.all([
        statistiquesService.getAuditNotesOcr({ limit: 50 }),
        statistiquesService.getAuditNotesDevoirs({ limit: 50 }),
      ]);
      setData(resE);
      setDataDev(resD);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleReparerA1 = async () => {
    if (!data || data.A1_notes_ocr_sans_db.count === 0) return;
    if (!window.confirm(
      `Créer les ${data.A1_notes_ocr_sans_db.count} NoteExamen manquantes ?\n\n` +
      `Cette action ne touchera AUCUNE note existante.\n` +
      `Les résultats des étudiants impactés seront recalculés automatiquement.`
    )) return;
    setReparing(true);
    setMsg(null);
    try {
      const res = await statistiquesService.reparerAuditA1({});
      setMsg(`✓ ${res.message}`);
      await fetchData();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erreur de réparation');
    } finally {
      setReparing(false);
    }
  };

  const handleReparerD1 = async () => {
    if (!dataDev || dataDev.D1_notes_ocr_sans_db.count === 0) return;
    if (!window.confirm(
      `Créer les ${dataDev.D1_notes_ocr_sans_db.count} NoteDevoir manquantes ?\n\n` +
      `Cette action ne touchera AUCUNE note existante.\n` +
      `Les résultats des étudiants impactés seront recalculés automatiquement.`
    )) return;
    setReparingD(true);
    setMsg(null);
    try {
      const res = await statistiquesService.reparerAuditDevoirsD1({});
      setMsg(`✓ ${res.message}`);
      await fetchData();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || 'Erreur de réparation');
    } finally {
      setReparingD(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <ShieldAlert className="text-amber-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Audit Notes OCR
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Détection des incohérences entre relevés validés et notes en base
            </p>
          </div>
        </div>
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-sm rounded-lg hover:bg-gray-200">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Rafraîchir
        </button>
      </div>

      {msg && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 rounded text-sm">
          {msg}
        </div>
      )}
      {err && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 rounded text-sm">
          {err}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader size={20} className="animate-spin mr-2" /> Analyse en cours…
        </div>
      )}

      {!loading && data && dataDev && data.total_alertes_critiques === 0 && dataDev.total_alertes_critiques === 0 && (
        <div className="p-6 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3">
          <CheckCircle2 className="text-green-600" size={28} />
          <div>
            <h2 className="font-bold text-green-800 dark:text-green-200">Aucune alerte critique</h2>
            <p className="text-sm text-green-700 dark:text-green-300">
              Examens et devoirs : toutes les notes des relevés OCR validés sont correctement enregistrées en base.
            </p>
          </div>
        </div>
      )}

      {/* Onglets Examens / Devoirs */}
      {!loading && (data || dataDev) && (
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('examens')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'examens'
                ? 'border-amber-600 text-amber-700 dark:text-amber-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Examens
            {data && data.total_alertes_critiques > 0 && (
              <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full text-[10px] font-bold">
                {data.total_alertes_critiques}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('devoirs')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'devoirs'
                ? 'border-amber-600 text-amber-700 dark:text-amber-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Devoirs
            {dataDev && dataDev.total_alertes_critiques > 0 && (
              <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full text-[10px] font-bold">
                {dataDev.total_alertes_critiques}
              </span>
            )}
          </button>
        </div>
      )}

      {!loading && data && activeTab === 'examens' && (
        <>
          {/* Cartes synthèse */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <SyntheseCard
              color="red" icon={<FileWarning size={18} />}
              label="Notes OCR manquantes en base (A1)"
              count={data.A1_notes_ocr_sans_db.count}
              critique
            />
            <SyntheseCard
              color="orange" icon={<AlertTriangle size={18} />}
              label="Lignes OCR orphelines (A2)"
              count={data.A2_lignes_orphelines.count}
            />
            <SyntheseCard
              color="amber" icon={<AlertTriangle size={18} />}
              label="Drift OCR ≠ DB (A4)"
              count={data.A4_drift_valeurs.count}
            />
            <SyntheseCard
              color="blue" icon={<Clock size={18} />}
              label="Relevés en attente de validation (A5)"
              count={data.A5_releves_en_attente.count}
              info
            />
          </div>

          {/* A1 — réparation principale */}
          {data.A1_notes_ocr_sans_db.count > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-red-300 dark:border-red-800 overflow-hidden">
              <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-bold text-red-800 dark:text-red-200">
                    A1 · {data.A1_notes_ocr_sans_db.count} notes OCR manquantes en base
                  </h3>
                  <p className="text-xs text-red-700 dark:text-red-400">
                    Ces étudiants apparaissent dans le relevé OCR mais n'ont pas de NoteExamen.
                    À réparer avant délibération.
                  </p>
                </div>
                <button onClick={handleReparerA1} disabled={reparing}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg disabled:opacity-50">
                  {reparing ? <Loader size={14} className="animate-spin" /> : <Wrench size={14} />}
                  Réparer toutes les notes manquantes
                </button>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Relevé</th>
                      <th className="px-3 py-2 text-left">Matière</th>
                      <th className="px-3 py-2 text-center">Statut</th>
                      <th className="px-3 py-2 text-center">Manquantes</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {data.A1_notes_ocr_sans_db.releves.map(r => (
                      <tr key={r.releve_id}>
                        <td className="px-3 py-2 font-mono text-xs">{r.code_releve}</td>
                        <td className="px-3 py-2">{r.matiere_nom || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                            r.status === 'valide'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}>{r.status}</span>
                        </td>
                        <td className="px-3 py-2 text-center font-bold text-red-600">{r.nb_manquantes}</td>
                        <td className="px-3 py-2">
                          <Link to={`/ocr/correction-v2/${r.releve_id}`}
                            className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                            <ExternalLink size={11} /> Ouvrir
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* A4 — drift de valeurs */}
          {data.A4_drift_valeurs.count > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-200 dark:border-amber-800 overflow-hidden">
              <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20">
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-200">
                  A4 · {data.A4_drift_valeurs.count} notes avec valeur différente entre OCR et DB
                </h3>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  La note dans le relevé OCR diffère de celle en base. Vérifier laquelle est correcte.
                </p>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Étudiant</th>
                      <th className="px-3 py-2 text-left">Matière</th>
                      <th className="px-3 py-2 text-center">OCR</th>
                      <th className="px-3 py-2 text-center">DB</th>
                      <th className="px-3 py-2 text-center">Écart</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {data.A4_drift_valeurs.cas.map(r => (
                      <tr key={r.ligne_id}>
                        <td className="px-3 py-2">
                          <div className="text-xs font-mono text-gray-500">{r.matricule}</div>
                          <div>{r.nom} <span className="text-xs text-gray-500">· {r.classe}</span></div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.matiere_code}</td>
                        <td className="px-3 py-2 text-center font-bold text-blue-600">{r.note_ocr.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center font-bold text-green-600">{r.note_db.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center text-orange-600 font-mono text-xs">
                          ±{r.ecart.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* A5 — relevés en attente */}
          {data.A5_releves_en_attente.count > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-blue-800 overflow-hidden">
              <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20">
                <h3 className="text-sm font-bold text-blue-800 dark:text-blue-200">
                  A5 · {data.A5_releves_en_attente.count} relevés en attente de validation
                </h3>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Ces relevés OCR ont été traités mais pas encore validés. Les notes ne sont pas en base.
                </p>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Relevé</th>
                      <th className="px-3 py-2 text-left">Matière</th>
                      <th className="px-3 py-2 text-center">Lignes</th>
                      <th className="px-3 py-2 text-left">Date scan</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {data.A5_releves_en_attente.releves.map(r => (
                      <tr key={r.releve_id}>
                        <td className="px-3 py-2 font-mono text-xs">{r.code_releve}</td>
                        <td className="px-3 py-2">
                          <div className="text-xs font-mono text-gray-500">{r.matiere_code}</div>
                          <div>{r.matiere_nom || '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-center">{r.nb_lignes}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {r.date_scan ? new Date(r.date_scan).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Link to={`/ocr/correction-v2/${r.releve_id}`}
                            className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                            <ExternalLink size={11} /> Vérifier
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════ ONGLET DEVOIRS ═══════════════ */}
      {!loading && dataDev && activeTab === 'devoirs' && (
        <>
          {/* Cartes synthèse Devoirs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <SyntheseCard
              color="red" icon={<FileWarning size={18} />}
              label="Notes Devoirs OCR manquantes (D1)"
              count={dataDev.D1_notes_ocr_sans_db.count}
              critique
            />
            <SyntheseCard
              color="orange" icon={<AlertTriangle size={18} />}
              label="Lignes OCR orphelines (D2)"
              count={dataDev.D2_lignes_orphelines.count}
            />
            <SyntheseCard
              color="amber" icon={<AlertTriangle size={18} />}
              label="Drift OCR ≠ DB (D3)"
              count={dataDev.D3_drift_valeurs.count}
            />
            <SyntheseCard
              color="blue" icon={<Clock size={18} />}
              label="Relevés en attente (D4)"
              count={dataDev.D4_releves_en_attente.count}
              info
            />
          </div>

          {/* D1 — réparation principale */}
          {dataDev.D1_notes_ocr_sans_db.count > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-red-300 dark:border-red-800 overflow-hidden">
              <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-bold text-red-800 dark:text-red-200">
                    D1 · {dataDev.D1_notes_ocr_sans_db.count} notes Devoirs OCR manquantes
                  </h3>
                  <p className="text-xs text-red-700 dark:text-red-400">
                    Lignes OCR vérifiées (D1, D2 ou absent) qui n'ont pas de NoteDevoir en base.
                  </p>
                </div>
                <button onClick={handleReparerD1} disabled={reparingD}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg disabled:opacity-50">
                  {reparingD ? <Loader size={14} className="animate-spin" /> : <Wrench size={14} />}
                  Réparer toutes les notes manquantes
                </button>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Relevé</th>
                      <th className="px-3 py-2 text-left">Matière</th>
                      <th className="px-3 py-2 text-center">Statut</th>
                      <th className="px-3 py-2 text-center">Manquantes</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {dataDev.D1_notes_ocr_sans_db.releves.map(r => (
                      <tr key={r.releve_id}>
                        <td className="px-3 py-2 font-mono text-xs">{r.code_releve}</td>
                        <td className="px-3 py-2">{r.matiere_nom || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                            r.status === 'valide'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}>{r.status}</span>
                        </td>
                        <td className="px-3 py-2 text-center font-bold text-red-600">{r.nb_manquantes}</td>
                        <td className="px-3 py-2">
                          <Link to={`/ocr/verification-devoirs/${r.releve_id}`}
                            className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                            <ExternalLink size={11} /> Ouvrir
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* D2 — orphelines */}
          {dataDev.D2_lignes_orphelines.count > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-orange-200 dark:border-orange-800 overflow-hidden">
              <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/20">
                <h3 className="text-sm font-bold text-orange-800 dark:text-orange-200">
                  D2 · {dataDev.D2_lignes_orphelines.count} lignes OCR sans étudiant associé
                </h3>
                <p className="text-xs text-orange-700 dark:text-orange-400">
                  Le matricule détecté n'a pas pu être rattaché à un étudiant. À corriger manuellement dans la page de vérification.
                </p>
              </div>
              <div className="overflow-x-auto max-h-72">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Relevé</th>
                      <th className="px-3 py-2 text-center">Statut</th>
                      <th className="px-3 py-2 text-center">Lignes orphelines</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {dataDev.D2_lignes_orphelines.releves.map(r => (
                      <tr key={r.releve_id}>
                        <td className="px-3 py-2 font-mono text-xs">{r.code_releve}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{r.status}</span>
                        </td>
                        <td className="px-3 py-2 text-center font-bold text-orange-600">{r.nb}</td>
                        <td className="px-3 py-2">
                          <Link to={`/ocr/verification-devoirs/${r.releve_id}`}
                            className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                            <ExternalLink size={11} /> Corriger
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* D3 — drift de valeurs */}
          {dataDev.D3_drift_valeurs.count > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-200 dark:border-amber-800 overflow-hidden">
              <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20">
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-200">
                  D3 · {dataDev.D3_drift_valeurs.count} notes avec valeur différente entre OCR et DB
                </h3>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  La note D1 ou D2 du relevé OCR diffère de celle en base. Vérifier laquelle est correcte.
                </p>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Étudiant</th>
                      <th className="px-3 py-2 text-left">Matière</th>
                      <th className="px-3 py-2 text-center">D1 OCR</th>
                      <th className="px-3 py-2 text-center">D1 DB</th>
                      <th className="px-3 py-2 text-center">D2 OCR</th>
                      <th className="px-3 py-2 text-center">D2 DB</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {dataDev.D3_drift_valeurs.cas.map(r => {
                      const diffD1 = r.ocr_d1 != null && r.db_d1 != null && Math.abs(r.ocr_d1 - r.db_d1) > 0.01;
                      const diffD2 = r.ocr_d2 != null && r.db_d2 != null && Math.abs(r.ocr_d2 - r.db_d2) > 0.01;
                      return (
                        <tr key={r.ligne_id}>
                          <td className="px-3 py-2">
                            <div className="text-xs font-mono text-gray-500">{r.matricule}</div>
                            <div>{r.nom} <span className="text-xs text-gray-500">· {r.classe}</span></div>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{r.matiere_code}</td>
                          <td className={`px-3 py-2 text-center font-bold ${diffD1 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {r.ocr_d1 != null ? r.ocr_d1.toFixed(2) : '—'}
                          </td>
                          <td className={`px-3 py-2 text-center font-bold ${diffD1 ? 'text-green-600' : 'text-gray-400'}`}>
                            {r.db_d1 != null ? r.db_d1.toFixed(2) : '—'}
                          </td>
                          <td className={`px-3 py-2 text-center font-bold ${diffD2 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {r.ocr_d2 != null ? r.ocr_d2.toFixed(2) : '—'}
                          </td>
                          <td className={`px-3 py-2 text-center font-bold ${diffD2 ? 'text-green-600' : 'text-gray-400'}`}>
                            {r.db_d2 != null ? r.db_d2.toFixed(2) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* D4 — relevés en attente */}
          {dataDev.D4_releves_en_attente.count > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-blue-800 overflow-hidden">
              <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20">
                <h3 className="text-sm font-bold text-blue-800 dark:text-blue-200">
                  D4 · {dataDev.D4_releves_en_attente.count} relevés Devoirs en attente de validation
                </h3>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Ces relevés OCR ont été traités mais pas encore validés. Les notes ne sont pas en base.
                </p>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Relevé</th>
                      <th className="px-3 py-2 text-left">Matière</th>
                      <th className="px-3 py-2 text-center">Lignes</th>
                      <th className="px-3 py-2 text-left">Date scan</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {dataDev.D4_releves_en_attente.releves.map(r => (
                      <tr key={r.releve_id}>
                        <td className="px-3 py-2 font-mono text-xs">{r.code_releve}</td>
                        <td className="px-3 py-2">
                          <div className="text-xs font-mono text-gray-500">{r.matiere_code}</div>
                          <div>{r.matiere_nom || '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-center">{r.nb_lignes}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {r.date_scan ? new Date(r.date_scan).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Link to={`/ocr/verification-devoirs/${r.releve_id}`}
                            className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                            <ExternalLink size={11} /> Vérifier
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {dataDev.total_alertes_critiques === 0 && (
            <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
              <CheckCircle2 size={20} /> Aucune anomalie sur les relevés de devoirs.
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SyntheseCard: React.FC<{
  color: 'red' | 'orange' | 'amber' | 'blue';
  icon: React.ReactNode;
  label: string;
  count: number;
  critique?: boolean;
  info?: boolean;
}> = ({ color, icon, label, count, critique, info }) => {
  const colors = {
    red: 'bg-red-50 border-red-300 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
    orange: 'bg-orange-50 border-orange-300 text-orange-700 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300',
    amber: 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300',
    blue: 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300',
  }[color];
  return (
    <div className={`p-4 border-2 rounded-xl ${colors}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-3xl font-bold">{count}</span>
        {count > 0 && critique && (
          <span className="text-[10px] font-bold uppercase">À RÉPARER</span>
        )}
        {count === 0 && (
          <CheckCircle2 size={20} className="text-green-500" />
        )}
        {info && count > 0 && (
          <span className="text-[10px] font-medium opacity-70">À traiter</span>
        )}
      </div>
    </div>
  );
};

export default AuditNotesOCR;
